/**
 * Derived indicators: the things the raw KSH series cannot say on their own.
 *
 * Guest-nights alone answer "how much traffic", never "is it full" or "is it
 * growing faster than the beds it needs". Those need the supply side
 * (capacity, KSH tur0088/tur0033) held against demand, which is what this
 * module does.
 *
 * Everything here is a pure function of RealData so it can be unit-tested
 * without a browser, and so the thresholds behind any verdict stay visible
 * rather than being buried in rendering code.
 */
import type { CapacityByYear, PeriodKey } from '@/types/data'
import type { RealData } from './realData'

/** Days in a period key: `YYYY` → 365/366, `YYYY-MM` → that month's length. */
export function periodDays(period: PeriodKey): number {
  const year = Number(period.slice(0, 4))
  if (period.length === 4) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
    return leap ? 366 : 365
  }
  return new Date(year, Number(period.slice(5, 7)), 0).getDate()
}

/**
 * Bed places for a county in the year a period falls in.
 *
 * KSH counts capacity once a year (July 31), so a monthly period resolves to
 * its own year's snapshot — the closest honest denominator available, and
 * exact for July and August.
 */
export function capacityFor(
  capacity: CapacityByYear,
  countySlug: string,
  period: PeriodKey,
): number | null {
  return capacity[period.slice(0, 4)]?.byCounty[countySlug as never] ?? null
}

/**
 * Bed utilisation, 0–100.
 *
 * Beds, not rooms: a room is rarely sold to two people every night, so even a
 * sold-out peak season lands well under 100. Useful for comparing counties and
 * tracking a county over time; misleading if read as "percent full".
 */
export function occupancy(nights: number | null, beds: number | null, days: number): number | null {
  if (nights == null || beds == null || beds <= 0 || days <= 0) return null
  return (nights / (beds * days)) * 100
}

export type SupplySignal = 'tight' | 'loose' | 'balanced'

export interface CountyIndicators {
  slug: string
  name: string
  /** Latest complete year in the window. */
  nights: number | null
  capacity: number | null
  hotels: number | null
  /** Whole-year utilisation. Understates seasonal counties — see occupancyPeak. */
  occupancyYear: number | null
  /**
   * July–August utilisation. The honest one: capacity is a July 31 snapshot,
   * so this is the window where supply and demand are measured at the same
   * moment.
   */
  occupancyPeak: number | null
  /** Nights growth across the window, %. */
  growth: number | null
  /** Change in the county's share of national nights, percentage points. */
  shareShift: number | null
  /** Bed-place growth across the window, %. */
  capacityGrowth: number | null
  /** growth − capacityGrowth, percentage points. Positive = demand outpacing beds. */
  gap: number | null
  /** Growth of the shoulder months (Apr, May, Sep, Oct), %. Season lengthening. */
  shoulderGrowth: number | null
  foreignShare: number | null
  signal: SupplySignal | null
}

export interface IndicatorSet {
  from: PeriodKey
  to: PeriodKey
  counties: CountyIndicators[]
  /** Median peak utilisation — the reference the signal is judged against. */
  medianOccupancyPeak: number | null
}

const SHOULDER_MONTHS = ['04', '05', '09', '10']
const PEAK_MONTHS = ['07', '08']

function nightsOf(data: RealData, ci: number, gran: 'm' | 'y', idx: number): number | null {
  const src = gran === 'm' ? data.monthly : data.yearly
  const d = src.nightsD[ci]?.[idx] ?? null
  const f = src.nightsF[ci]?.[idx] ?? null
  return d != null && f != null ? d + f : null
}

function sumMonths(data: RealData, ci: number, year: string, months: string[]): number | null {
  let sum = 0
  let seen = 0
  for (const m of months) {
    const idx = data.monthlyPeriods.indexOf(`${year}-${m}`)
    if (idx < 0) continue
    const v = nightsOf(data, ci, 'm', idx)
    if (v == null) continue
    sum += v
    seen++
  }
  // All-or-nothing: a partial sum would silently understate the comparison.
  return seen === months.length ? sum : null
}

/**
 * Compute the indicator set over a window ending at the latest yearly period.
 *
 * The baseline defaults to two years back rather than to the start of the
 * series on purpose. 2021 is both the first year of the current methodology
 * and a COVID year, and Budapest — 87% foreign — was still flat on the floor
 * then: measured from 2021 it "grows" 234% and takes 18 points of national
 * share, which mechanically pushes every other county's share down. That
 * measures recovery, not development.
 */
export function computeIndicators(data: RealData, windowYears = 2): IndicatorSet {
  const years = data.yearlyPeriods
  const to = years.at(-1) ?? ''
  const toIdx = years.length - 1
  const fromIdx = Math.max(0, toIdx - windowYears)
  const from = years[fromIdx] ?? to

  const nationalAt = (idx: number): number => {
    let sum = 0
    for (let ci = 0; ci < data.counties.length; ci++) sum += nightsOf(data, ci, 'y', idx) ?? 0
    return sum
  }
  const natTo = nationalAt(toIdx)
  const natFrom = nationalAt(fromIdx)

  const counties: CountyIndicators[] = data.counties.map((county, ci) => {
    const nTo = nightsOf(data, ci, 'y', toIdx)
    const nFrom = nightsOf(data, ci, 'y', fromIdx)
    const capTo = capacityFor(data.capacity, county.slug, to)
    const capFrom = capacityFor(data.capacity, county.slug, from)
    const hotels = data.capacity[to]?.hotelsByCounty[county.slug as never] ?? null

    const growth = nTo != null && nFrom ? (nTo / nFrom - 1) * 100 : null
    const capacityGrowth = capTo != null && capFrom ? (capTo / capFrom - 1) * 100 : null
    const peakNights = sumMonths(data, ci, to, PEAK_MONTHS)
    const peakDays = PEAK_MONTHS.reduce((a, m) => a + periodDays(`${to}-${m}`), 0)

    const shoulderTo = sumMonths(data, ci, to, SHOULDER_MONTHS)
    const shoulderFrom = sumMonths(data, ci, from, SHOULDER_MONTHS)

    const foreign = data.yearly.nightsF[ci]?.[toIdx] ?? null

    return {
      slug: county.slug,
      name: county.name,
      nights: nTo,
      capacity: capTo,
      hotels,
      occupancyYear: occupancy(nTo, capTo, periodDays(to)),
      occupancyPeak: occupancy(peakNights, capTo, peakDays),
      growth,
      shareShift:
        nTo != null && nFrom != null && natTo && natFrom
          ? (nTo / natTo - nFrom / natFrom) * 100
          : null,
      capacityGrowth,
      gap: growth != null && capacityGrowth != null ? growth - capacityGrowth : null,
      shoulderGrowth:
        shoulderTo != null && shoulderFrom ? (shoulderTo / shoulderFrom - 1) * 100 : null,
      foreignShare: nTo && foreign != null ? (foreign / nTo) * 100 : null,
      signal: null,
    }
  })

  // The signal is deliberately relative, not an absolute cutoff: bed
  // utilisation has no universal "full" level (it depends on room occupancy
  // habits and on how seasonal the county is), so a county is judged against
  // what the rest of the country is doing in the same season.
  const peaks = counties
    .map((c) => c.occupancyPeak)
    .filter((v): v is number => v != null)
    .sort((a, b) => a - b)
  // A true median, averaging the middle pair on an even count. Taking the
  // upper middle instead makes the busiest county fail its own `> median`
  // test whenever the count is even.
  const median = peaks.length
    ? peaks.length % 2
      ? (peaks[(peaks.length - 1) / 2] ?? null)
      : (peaks[peaks.length / 2 - 1]! + peaks[peaks.length / 2]!) / 2
    : null

  if (median != null) {
    for (const c of counties) {
      if (c.occupancyPeak == null || c.gap == null) continue
      if (c.occupancyPeak > median && c.gap > 0) c.signal = 'tight'
      else if (c.occupancyPeak < median && c.gap < 0) c.signal = 'loose'
      else c.signal = 'balanced'
    }
  }

  return { from, to, counties, medianOccupancyPeak: median }
}

export const SIGNAL_LABEL: Record<SupplySignal, string> = {
  tight: 'szűkül a kínálat',
  loose: 'lazul a kihasználtság',
  balanced: 'kiegyensúlyozott',
}
